import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { atxIsMobile$ } from '@epicuro-next/ataraxia/breakpoint';
import {
  IFilter,
  SpritesPaths,
  SPRITES_PATHS_TOKEN,
} from '@epicuro-next/ataraxia/models';
import {
  ConfigurationsService,
  NAMESPACE_PRODUCT_BASKET,
  PBasketConfigurationKeys,
  PBasketConfigurations,
  pBasketConfigurationsDefaultValues,
} from '@epicuro-next/platform/configuration';
import { selectActiveAccountCurrency } from '@epicuro-next/state/accounts-state';
import {
  isdDatesFilterId,
  loadFilters,
  selectActiveFilters,
  selectAvailableAndActiveValues,
  selectFilterLoadError,
  selectFiltersOpenedIdsRule,
  selectHiddenFilterTitleIds,
  selectLastActivatedFilter,
  selectMultiRangeSliderInputsRule,
  selectProductBasketFiltersLoaded,
  toggleFilter,
  wspFilterId,
} from '@epicuro-next/state/p-basket-state';
import { selectShowWholeSalePrices } from '@epicuro-next/state/user-state';
import {
  isDateRangeTypeFilter,
  isRangeTypeFilter,
  objectKeysToLowercase,
} from '@epicuro-next/utilities';
import { select, Store } from '@ngrx/store';
import {
  Filter,
  FilterDisplayValueTransformOption,
  FilterType,
} from 'graphql-api/gql-api-interfaces';
import {
  combineLatest,
  filter as rxjsFilter,
  map,
  Observable,
  of,
  shareReplay,
  Subject,
  take,
  takeUntil,
} from 'rxjs';

const DEFAULT_KEY = 'default';

@Component({
  selector: 'epc-search-filters-container',
  templateUrl: './search-filters-container.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./search-filters-container.component.scss'],
  standalone: false,
})
export class SearchFiltersContainerComponent implements OnInit, OnDestroy {
  public disableCollapse = input(false);

  public isMobile$ = atxIsMobile$;

  public skeletonFilters$ = of(Array.from(new Array(7)).fill({}));

  public openedFields: { [key: string]: boolean } = {};

  public readonly selectFilterLoadError$ = this.store.select(
    selectFilterLoadError,
  );

  private destroy$ = new Subject<void>();

  public readonly hiddenFilterTitleIds = this.store.selectSignal(
    selectHiddenFilterTitleIds,
  );
  public readonly openedFilterIds = this.store.selectSignal(
    selectFiltersOpenedIdsRule,
  );

  /* Active Account Currency */
  public activeAccountCurrency$ = this.store.pipe(
    select(selectActiveAccountCurrency),
  );
  /** Available filters */
  public availableFilters$: Observable<Filter[]> = combineLatest([
    this.store.pipe(select(selectAvailableAndActiveValues)),
    this.activeAccountCurrency$,
    this.store.pipe(select(selectShowWholeSalePrices)),
  ]).pipe(
    map(([filters, activeAccountCurrency, showWspPrice]) =>
      filters
        .filter((filter) =>
          this.shouldRenderFilter(filter, activeAccountCurrency),
        )
        .map((filter) =>
          this.isRangeFilter(filter) && !showWspPrice
            ? {
                ...filter,
                subFilters: (filter.subFilters ?? []).filter(
                  ({ fieldId }) => fieldId !== wspFilterId,
                ),
              }
            : filter,
        ),
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  /** Filters loading state */
  public loadedFilter$ = this.store.pipe(
    select(selectProductBasketFiltersLoaded),
  );

  public multiRangeSliderInputs$ = this.store.pipe(
    select(selectMultiRangeSliderInputsRule),
  );

  /** Active filters values */
  public activeFilters$ = this.store.pipe(select(selectActiveFilters));
  /** Last activated filter */
  public lastActivatedFilter$ = this.store.pipe(
    select(selectLastActivatedFilter),
    rxjsFilter(Boolean),
  );

  public readonly filterColorKey$: Observable<string> =
    this.configsService.getConfigValue<
      PBasketConfigurations,
      PBasketConfigurationKeys.FilterColorsKey
    >({
      namespace: NAMESPACE_PRODUCT_BASKET,
      featureName: PBasketConfigurationKeys.FilterColorsKey,
      defaultValue:
        pBasketConfigurationsDefaultValues[
          PBasketConfigurationKeys.FilterColorsKey
        ],
    });

  public readonly filterColorValues$: Observable<{
    [key: string]: string;
  }> = this.configsService
    .getConfigValue<
      PBasketConfigurations,
      PBasketConfigurationKeys.FilterColors
    >({
      namespace: NAMESPACE_PRODUCT_BASKET,
      featureName: PBasketConfigurationKeys.FilterColors,
      defaultValue:
        pBasketConfigurationsDefaultValues[
          PBasketConfigurationKeys.FilterColors
        ],
    })
    .pipe(
      map((value) => JSON.parse(value)),
      map(objectKeysToLowercase),
    );

  private filterIconsValues$: Observable<{
    [key: string]: { [key: string]: string };
  }> = this.configsService
    .getConfigValue<
      PBasketConfigurations,
      PBasketConfigurationKeys.FilterIcons
    >({
      namespace: NAMESPACE_PRODUCT_BASKET,
      featureName: PBasketConfigurationKeys.FilterIcons,
      defaultValue:
        pBasketConfigurationsDefaultValues[
          PBasketConfigurationKeys.FilterIcons
        ],
    })
    .pipe(map((value) => JSON.parse(value)));

  constructor(
    @Inject(SPRITES_PATHS_TOKEN) public spritesPaths: SpritesPaths,
    private store: Store,
    private configsService: ConfigurationsService,
  ) {}

  public ngOnInit(): void {
    this.refreshAccordions();

    this.listenToLinkedFields();
  }

  public ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public isFilterActive(fieldId: string, value: string, mergedValues = false) {
    return this.activeFilters$.pipe(
      map((filters) => {
        if (mergedValues) {
          const splittedValues = value.split('|');
          return filters.some(
            (filter) =>
              filter.fieldId === fieldId &&
              splittedValues.includes(filter.value),
          );
        } else {
          return filters.some(
            (filter) => filter.fieldId === fieldId && filter.value === value,
          );
        }
      }),
    );
  }
  public toggleFilter(fieldId: string, value: string, dataType?: string) {
    this.store.dispatch(
      toggleFilter({
        fieldId,
        value,
        dataType,
        replaceUrl: false,
      }),
    );
  }

  public activeRangeFilter(
    fieldId: string,
    activeFilters: IFilter[],
    availableFilters: Filter[],
  ): IFilter | undefined {
    const filter = availableFilters.find((x) => x.fieldId === fieldId);
    const subFilters = filter?.subFilters;

    return (
      activeFilters.find((x) =>
        subFilters?.find((y) => y.fieldId === x.fieldId),
      ) ?? activeFilters.find((x) => filter?.fieldId === x.fieldId)
    );
  }

  public refreshAccordions() {
    combineLatest([this.activeFilters$, this.availableFilters$])
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(([activeFilters, filters]) => {
        // get all active filters
        const activeFiltersMap = activeFilters.reduce(
          (acc: { [key: string]: string[] }, { fieldId }) => ({
            ...acc,
            [fieldId]: true,
          }),
          {},
        );

        // get filters that should be opened by default
        const defaultOpenedFields = filters.reduce((acc, curr) => {
          if (curr.collapse?.opened) {
            return { ...acc, [curr.fieldId]: true };
          } else {
            return acc;
          }
        }, {});

        this.openedFields = {
          ...activeFiltersMap,
          ...defaultOpenedFields,
        };

        // get linked fields that should be opened
        this.updateLinkedFields(activeFilters, filters);
      });
  }

  public isDateRangeFilter(filterItem: Filter) {
    return (
      isDateRangeTypeFilter(filterItem) ||
      filterItem.fieldId === isdDatesFilterId
    );
  }

  public getActiveFiltersByFieldId(fieldId: string, activeFilters: IFilter[]) {
    return activeFilters.filter((x) => x.fieldId === fieldId);
  }

  public isFilterDisabled(fieldId: string) {
    return combineLatest([this.loadedFilter$, this.lastActivatedFilter$]).pipe(
      map(
        ([filtersLoaded, lastActivatedFilter]) =>
          !filtersLoaded && lastActivatedFilter !== fieldId,
      ),
    );
  }

  public getFilterColorValue(
    filterColorValues: Record<string, string>,
    value: string,
  ) {
    return (
      filterColorValues[value.toLowerCase()] ?? filterColorValues[DEFAULT_KEY]
    );
  }

  public getFilterIconValue(fieldId: string, filterValue: string) {
    return this.filterIconsValues$.pipe(
      map(
        (iconValues) =>
          iconValues?.[fieldId]?.[filterValue] ??
          iconValues?.[fieldId]?.[DEFAULT_KEY] ??
          null,
      ),
    );
  }

  public isFilterColor(filterItem: Filter, filterColorKey: string) {
    return (
      filterItem.fieldId === filterColorKey ||
      filterItem.displayValueTransform ===
        FilterDisplayValueTransformOption.Color
    );
  }

  public isRangeFilter(filterItem: Filter) {
    return (
      'priceRange' === filterItem.fieldId ||
      isRangeTypeFilter(filterItem) ||
      filterItem.subFilters?.some(isRangeTypeFilter)
    );
  }

  public shouldRenderFilter(
    filter: Filter,
    activeAccountCurrency: string | null,
  ) {
    if (!this.isRangeFilter(filter)) {
      return true;
    }
    return (
      this.isRangeFilter(filter) &&
      activeAccountCurrency &&
      activeAccountCurrency?.length > 0
    );
  }

  public onLoadFilters() {
    this.store.dispatch(loadFilters());
  }

  public trackByIndex(index: number) {
    return index;
  }

  public getRangeFilterUnit(filter: Filter, currency: string) {
    return FilterType.Group === filter.type ? currency : undefined;
  }

  private updateLinkedFields(
    activeFilters: IFilter[],
    availableFilters: Filter[],
  ) {
    const activeFiltersMap = activeFilters.reduce(
      (acc: { [key: string]: string[] }, { fieldId }) => ({
        ...acc,
        [fieldId]: true,
      }),
      {},
    );

    const linkedFieldsMap: {
      [key: string]: string[];
    } = availableFilters.reduce((acc, filter) => {
      return filter.collapse?.opened
        ? { ...acc, [filter.fieldId]: filter.collapse.linkedFields }
        : acc;
    }, {});

    Object.keys(linkedFieldsMap).forEach((fieldId) => {
      if (activeFiltersMap[fieldId]) {
        this.openedFields = {
          ...this.openedFields,
          ...linkedFieldsMap[fieldId].reduce(
            (acc, curr) => ({ ...acc, [curr]: true }),
            {},
          ),
        };
      }
    });
  }

  private listenToLinkedFields() {
    combineLatest([this.activeFilters$, this.availableFilters$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([activeFilters, filters]) => {
        return this.updateLinkedFields(activeFilters, filters);
      });
  }
}
